import { useState, type ReactNode } from "react";

import type { Deviation, Direction, SpacetimeWindow } from "../contract/types";
import { useBackend } from "../app/BackendProvider";
import { CollapsedRail, MobileDrawer } from "../app/CollapsiblePanel";
import { useStreamContext } from "../app/StreamProvider";
import { DegradedBanner } from "../app/DegradedBanner";
import { usePersistentFlag } from "../app/persistentUi";
import { useIsMobile } from "../app/useIsMobile";
import { useSpacetimeDefaultLineId } from "../network/bits";
import { CockpitTopbar } from "./CockpitTopbar";
import { CondensedSynoptic } from "./CondensedSynoptic";
import { DeviationQueue } from "./DeviationQueue";
import { IndicatorsBar } from "./IndicatorsBar";
import { SpacetimeChart } from "./SpacetimeChart";
import { ToolsPanel } from "./ToolsPanel";

/**
 * Écran principal — cockpit 1a. Trois colonnes recomposées après l'abandon du
 * lot d'action (docs/01-analyse-maquettes.md §7) :
 *
 *   Col 1  Avance/Retard                       (repliable)
 *   Col 2  Graphique espace-temps · indicateurs · synoptique condensé
 *   Col 3  Outils — détail course · retournement · croisement   (repliable)
 *
 * Les deux colonnes latérales se replient pour dégager le centre ; l'état est
 * retenu jusqu'au prochain vidage du cache. Aucune interface d'écriture hormis
 * l'acquittement d'un écart (F4.5).
 *
 * **Mobile** (demande utilisateur 2026-09-11, < 769px) : trois colonnes fixes
 * ne tiennent pas sur un téléphone — le centre (espace-temps/synoptique)
 * occupe toute la largeur, Avance/Retard et Outils s'ouvrent en **tiroir
 * plein écran** (`MobileDrawer`) depuis une barre d'accès rapide, sans lien
 * avec l'état plié/déplié persisté du desktop (état local, toujours fermé au
 * chargement).
 */
export function Cockpit() {
  const backend = useBackend();
  const { status } = useStreamContext();
  const isMobile = useIsMobile();

  const [leftCollapsed, setLeftCollapsed] = usePersistentFlag("sentinelle.cockpit.queue.collapsed");
  const [rightCollapsed, setRightCollapsed] = usePersistentFlag("sentinelle.cockpit.tools.collapsed");
  const [synopticCollapsed, setSynopticCollapsed] = usePersistentFlag("sentinelle.cockpit.synoptic.collapsed");
  const [mobileDrawer, setMobileDrawer] = useState<"queue" | "tools" | null>(null);

  const [selectedJourneyId, setSelectedJourneyId] = useState<string | null>(null);
  // Aucune ligne codée en dur (CDC §1.1) : J.11 — on démarre sur
  // `config.defaultSpacetimeLineId` s'il est fourni, sinon sur la première ligne
  // autorisée pour le poste actif, comme les vues de l'écran réseau.
  const defaultLineId = useSpacetimeDefaultLineId();
  const [spacetime, setSpacetime] = useState<{ lineId: string; direction: Direction; window: SpacetimeWindow }>({
    lineId: defaultLineId,
    direction: 0,
    window: "30m",
  });

  function onSelectDeviation(deviation: Deviation) {
    setSelectedJourneyId(deviation.journeyId);
    setSpacetime((s) => ({ ...s, lineId: deviation.lineId, direction: deviation.direction }));
  }

  const gridTemplateColumns = [
    leftCollapsed ? "36px" : "minmax(280px, 300px)",
    "minmax(0, 1fr)",
    rightCollapsed ? "36px" : "minmax(330px, 372px)",
  ].join(" ");

  return (
    <div className="mae-app">
      <CockpitTopbar streamStatus={status} />
      <DegradedBanner />
      {import.meta.env.DEV && backend.usesMocks ? (
        <div className="ovl" style={{ background: "var(--sentinelle-signal-bg)", color: "var(--sentinelle-warn)", padding: "3px 16px" }}>
          Mode mocks — données de démonstration, le frontend n'attend pas le backend
        </div>
      ) : null}

      {isMobile ? (
        <div
          style={{
            display: "flex",
            gap: 8,
            padding: "8px 12px",
            flex: "none",
            borderBottom: "1px solid var(--sentinelle-rule)",
            background: "var(--sentinelle-surface)",
          }}
        >
          <button type="button" className="button is-small" onClick={() => setMobileDrawer("queue")}>
            Avance/Retard
          </button>
          <button type="button" className="button is-small" onClick={() => setMobileDrawer("tools")}>
            Outils
          </button>
        </div>
      ) : null}

      <div
        className="mae-fill"
        style={
          isMobile
            ? { display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }
            : { display: "grid", gridTemplateColumns, overflow: "hidden" }
        }
      >
        {!isMobile &&
          (leftCollapsed ? (
            <CollapsedRail label="Avance/Retard" side="right" onExpand={() => setLeftCollapsed(false)} />
          ) : (
            <Column border="right" scroll={false}>
              <DeviationQueue
                selectedJourneyId={selectedJourneyId}
                onSelect={onSelectDeviation}
                onCollapse={() => setLeftCollapsed(true)}
              />
            </Column>
          ))}

        <Column>
          <Section grow>
            <SpacetimeChart
              lineId={spacetime.lineId}
              direction={spacetime.direction}
              window={spacetime.window}
              onWindowChange={(window) => setSpacetime((s) => ({ ...s, window }))}
              onLineChange={(lineId) => setSpacetime((s) => ({ ...s, lineId }))}
              onDirectionChange={(direction) => setSpacetime((s) => ({ ...s, direction }))}
            />
          </Section>
          <IndicatorsBar lineId={spacetime.lineId} direction={spacetime.direction} />
          <Section>
            <CondensedSynoptic
              onSelectJourney={setSelectedJourneyId}
              collapsed={synopticCollapsed}
              onToggleCollapsed={() => setSynopticCollapsed(!synopticCollapsed)}
            />
          </Section>
        </Column>

        {!isMobile &&
          (rightCollapsed ? (
            <CollapsedRail label="Outils" side="left" onExpand={() => setRightCollapsed(false)} />
          ) : (
            <Column border="left" scroll={false}>
              <ToolsPanel
                selectedJourneyId={selectedJourneyId}
                onSelectJourney={setSelectedJourneyId}
                onCollapse={() => setRightCollapsed(true)}
              />
            </Column>
          ))}
      </div>

      {isMobile && mobileDrawer === "queue" ? (
        <MobileDrawer onClose={() => setMobileDrawer(null)}>
          <DeviationQueue
            selectedJourneyId={selectedJourneyId}
            onSelect={(d) => {
              onSelectDeviation(d);
              setMobileDrawer(null);
            }}
            onCollapse={() => setMobileDrawer(null)}
          />
        </MobileDrawer>
      ) : null}
      {isMobile && mobileDrawer === "tools" ? (
        <MobileDrawer side="right" onClose={() => setMobileDrawer(null)}>
          <ToolsPanel
            selectedJourneyId={selectedJourneyId}
            onSelectJourney={setSelectedJourneyId}
            onCollapse={() => setMobileDrawer(null)}
          />
        </MobileDrawer>
      ) : null}
    </div>
  );
}

function Column({
  children,
  border,
  scroll = true,
}: {
  children: ReactNode;
  border?: "left" | "right";
  scroll?: boolean;
}) {
  return (
    <div
      className={scroll ? "mae-scroll-y" : undefined}
      style={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        background: "var(--sentinelle-surface)",
        borderLeft: border === "left" ? "1px solid var(--sentinelle-rule)" : undefined,
        borderRight: border === "right" ? "1px solid var(--sentinelle-rule)" : undefined,
        overflow: scroll ? undefined : "hidden",
      }}
    >
      {children}
    </div>
  );
}

function Section({ children, grow }: { children: ReactNode; grow?: boolean }) {
  return (
    <section
      style={{
        borderBottom: "1px solid var(--sentinelle-rule)",
        padding: "12px 14px",
        flex: grow ? 1 : undefined,
        minHeight: grow ? 240 : undefined,
      }}
    >
      {children}
    </section>
  );
}
